import './MultiSelectFilter.scss';
import { RowData } from '@tanstack/react-table';
import { LegacyColumn } from '@tanstack/react-table/legacy';
import { useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { MdSearch } from 'react-icons/md';
import { Checkbox } from '@Components/FormCheck';
import { InteractiveSpan } from '@Components/InteractiveEl';
import { FilterDropdown } from './FilterDropdown';
import { normalizeFilterValue, stringifyFilterValue } from './hierarchical-table-utils';

interface MultiSelectFilterProps<TData extends RowData> {
  column: LegacyColumn<TData>;
  label: string;
}

interface ValueCount {
  value: string;
  count: number;
}

/**
 * Multi-select dropdown filter for string/categorical columns
 * - Shows unique values with counts from faceted row model
 * - Supports search within values
 * - Toggle all filtered values
 */
export const MultiSelectFilter = <TData extends RowData>({
  column,
  label
}: MultiSelectFilterProps<TData>) => {
  const [searchTerm, setSearchTerm] = useState('');

  const facetedUniqueValues = column.getFacetedUniqueValues();

  const filterValue = column.getFilterValue() as string[] | undefined;
  const selectedValues = useMemo(() => filterValue ?? [], [filterValue]);
  const selectedKeys = new Set(selectedValues.map(normalizeFilterValue));
  const isSelected = (value: string) => selectedKeys.has(normalizeFilterValue(value));

  const uniqueValues = useMemo((): ValueCount[] => {
    // Keyed by normalized value for case/whitespace-insensitive deduplication,
    // preserves original capitalization for display
    const valueCounts = new Map<string, ValueCount>();

    facetedUniqueValues.forEach((count, rawValue) => {
      const normalizedKey = normalizeFilterValue(rawValue);
      if (normalizedKey === null) {
        return;
      }
      const existing = valueCounts.get(normalizedKey);
      if (existing) {
        existing.count += count;
      } else {
        valueCounts.set(normalizedKey, { value: stringifyFilterValue(rawValue) ?? '', count });
      }
    });

    // Keep selected values visible (count 0) even when other columns' filters
    // removed all of their rows, so active selections can always be unchecked
    selectedValues.forEach(selected => {
      const normalizedKey = normalizeFilterValue(selected);
      if (normalizedKey !== null && !valueCounts.has(normalizedKey)) {
        valueCounts.set(normalizedKey, { value: selected, count: 0 });
      }
    });

    return Array.from(valueCounts.values()).sort((a, b) => b.count - a.count);
  }, [facetedUniqueValues, selectedValues]);

  const filteredValues = useMemo(() => {
    if (!searchTerm) {
      return uniqueValues;
    }
    const lowerSearch = searchTerm.toLowerCase();
    return uniqueValues.filter(v => v.value.toLowerCase().includes(lowerSearch));
  }, [uniqueValues, searchTerm]);

  /** Clear the filter when the selection covers every option (same as no filter) */
  const applySelection = (newSelected: string[]) => {
    const newKeys = new Set(newSelected.map(normalizeFilterValue));
    const coversAllOptions = uniqueValues.every(v => newKeys.has(normalizeFilterValue(v.value)));
    if (newSelected.length === 0 || coversAllOptions) {
      column.setFilterValue(undefined);
    } else {
      column.setFilterValue(newSelected);
    }
  };

  const handleToggle = (value: string) => {
    const key = normalizeFilterValue(value);
    applySelection(
      selectedKeys.has(key)
        ? selectedValues.filter(v => normalizeFilterValue(v) !== key)
        : [...selectedValues, value]
    );
  };

  const allFilteredChecked =
    selectedValues.length > 0 &&
    filteredValues.length > 0 &&
    filteredValues.every(v => isSelected(v.value));

  /** Toggle only the values matching the current search, preserving selections outside it */
  const handleToggleAll = () => {
    const filteredKeys = new Set(filteredValues.map(v => normalizeFilterValue(v.value)));
    if (allFilteredChecked) {
      applySelection(selectedValues.filter(v => !filteredKeys.has(normalizeFilterValue(v))));
    } else {
      const additions = filteredValues.filter(v => !isSelected(v.value)).map(v => v.value);
      applySelection([...selectedValues, ...additions]);
    }
  };

  return (
    <FilterDropdown
      label={label}
      active={column.getIsFiltered()}
      menuClassName='multi-select-filter-dropdown'
    >
      <div className='filter-search'>
        <MdSearch className='search-icon' />
        <Form.Control
          type='text'
          placeholder='Search...'
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onClick={e => e.stopPropagation()}
        />
      </div>

      <div className='filter-values'>
        <div className='filter-values-header'>
          <div className='header-value-with-checkbox'>
            <Checkbox checked={allFilteredChecked} onChange={handleToggleAll} />
            <span className='header-value'>Value</span>
          </div>
          <span className='header-count'>Count</span>
        </div>

        <div className='filter-value-list'>
          {filteredValues.length === 0 ? (
            <div className='no-results'>No matching values</div>
          ) : (
            filteredValues.map(({ value, count }) => (
              <div key={value} className='filter-row'>
                <Checkbox checked={isSelected(value)} onChange={() => handleToggle(value)} />
                <InteractiveSpan className='value-text' onClick={() => handleToggle(value)}>
                  {value}
                </InteractiveSpan>
                <span className='value-count'>{count}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </FilterDropdown>
  );
};
